import { Injectable } from '@angular/core';
import { LevelService, LevelSearch } from '@app/level/level.service';
import { ProgramCategoryService, ProgramCategorySearch } from '@app/program-category/program-category.service';
import { EnvironmentService, EnvironmentSearch } from '@app/environment/environment.service';
import { GoalService, GoalSearch } from '@app/goal/goal.service';
import { InstructorService } from '@app/instructor/instructor.service';
import { EquipmentService } from '@app/equipment/equipment.service';
import { Customer } from '@app/user/user.service';
import { FitApiClientService, FitList } from '@shared/fit-api-client.service';
import { Program, ProgramBulkStageRequest, ProgramResponse, ProgramSearch, MuscleGroup, ProgramPackage, ProgramStageKey, ProgramStageUpdate } from './types';
import { MuscleGroupService } from '@app/muscle-group/muscle-group.service';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { getBrowserTimezone } from '@shared/date-utils.service';
import { ProgramListEnrichmentService } from './program-list-enrichment.service';
import { ProgramTag, ProgramTagRequest, ProgramTagService } from './program-tag.service';

export { LevelSearch, ProgramCategorySearch, EnvironmentSearch, GoalSearch, Customer, MuscleGroup };

export type ProgramSource = 'all' | 'custom' | 'lean_on';

export type ProgramSortOrder = 'recently_updated' | 'newest' | 'oldest' | 'name_asc' | 'name_desc';

export interface ProgramSearchParams {
    search?: string;
    page?: number;
    per_page?: number;
    source?: ProgramSource;
    order?: ProgramSortOrder;
    tag_ids?: number[];
    program_category_ids?: number[];
    level_ids?: number[];
    program_length_filter_ids?: number[];
    environment_ids?: number[];
    equipment_ids?: number[];
    stage_ids?: number[];
    is_published?: boolean;
    is_free?: boolean;
}

export interface ProgramSourceCounts {
    all: number;
    custom: number;
    lean_on: number;
}

export interface User {
    apple_id: string | number;
    bio: null;
    cover_image_id: null;
    created_at: string;
    date_of_birth: string;
    dietary_goal_id: string | number;
    email: string;
    email_verified_at: string;
    exercise_environment_id: string | number;
    firebase_id: string;
    first_name: string;
    food_preference_id: string | number;
    gender: string;
    goal_id: string | number;
    google_id: string;
    has_seen_welcome_message: true;
    height_in_centimeters: string | number;
    id: number;
    image_id: number;
    is_active: boolean;
    is_survey_completed: boolean;
    last_name: string;
    level_id: string | number;
    metric_unit: string;
    phone_number: string | number;
    timezone: string;
    updated_at: string;
    weight_in_kilograms: string | number;
}

export interface WeekItemRequest {
    is_locked: boolean;
    order: number;
    training_id: number;
    is_rest_day: boolean;
    alternative_training_ids?: number[];
}

export interface ProgramWeekRequest {
    order: number;
    description: string;
    program_week_items: WeekItemRequest[];
}

export interface ProgramRequest {
    name: string;
    internal_name: string;
    description: string;
    gender: 'male' | 'female';
    language?: string | null;
    workout_days_per_week: number;
    video_id: number | null;
    image_id: number | null;
    is_published: boolean;
    publish_at?: string;
    is_free: boolean;
    is_tailor_made: boolean;
    is_coming_soon: boolean;
    follows_schedule: boolean;
    parent_id?: number;
    stripe_product_id?: string;
    stripe_payment_link?: string;
    shopify_product_id?: string;
    order?: number;
    category_ids: number[];
    level_ids: number[];
    exercise_environment_ids: number[];
    goal_ids: number[];
    instructors: ProgramInstructorRequest[];
    expected_results: string[];
    program_weeks: ProgramWeekRequest[];
    rest_day_image_id: number;
    muscle_group_ids: number[];
    gallery_ids: any;
    price?: string;
    currency_code?: string;
    questionnaire_id?: number;
    readiness_questionnaire_id?: number;
    include_in_survey: boolean;
    only_with_one_time_payment?: boolean;
    package_id?: number;
    number_of_renewals?: number | null;
    continue_renewing?: boolean;
    notify_coach_on_purchase?: boolean;
    program_packages?: ProgramPackage[];
    tag_ids?: number[];
    min_age?: number | null;
    max_age?: number | null;
    live_sync?: boolean;
}

interface ProgramInstructorRequest {
    id: number;
    sort_order: number;
}

@Injectable({ providedIn: 'root' })

export class ProgramService {
    constructor(
        private fitApi: FitApiClientService,
        private programCategoryService: ProgramCategoryService,
        private levelService: LevelService,
        private environmentService: EnvironmentService,
        private goalService: GoalService,
        private muscleGroupsService: MuscleGroupService,
        private instructorService: InstructorService,
        private equipmentService: EquipmentService,
        private listEnrichment: ProgramListEnrichmentService,
        private programTagService: ProgramTagService,
    ) { }

    public search(
        query: string,
        page: number,
        is_tailor_made: boolean = false,
        one_time_payment_only: boolean = false,
        order: string,
        author?: string
    ) {
        const params: any = {
            search: query,
            page: page.toString(),
            is_tailor_made,
            one_time_payment_only,
            order
        };

        if (author === 'coach') {
            params.is_by_coach = true;
        } else if (author === 'admin') {
            params.is_by_coach = false;
        }

        return this.fitApi.get<FitList<ProgramSearch>>(
            '/admin/program',
            params
        );
    }

    public searchPrograms(params: ProgramSearchParams = {}) {
        const apiParams = this.buildSearchParams(params);
        return this.fitApi.get<FitList<ProgramSearch>>('/admin/program', apiParams)
            .then(response => {
                if (response.error || !response.data) {
                    return response;
                }
                return { ...response, data: this.listEnrichment.enrichList(response.data) };
            });
    }

    public async getSourceCounts(): Promise<ProgramSourceCounts> {
        const { data, error } = await this.fitApi.get<ProgramSourceCounts>('/admin/program/source-counts');
        if (!error && data) {
            return data;
        }
        const [{ data: allData }, { data: customData }, { data: leanOnData }] = await Promise.all([
            this.searchPrograms({ page: 1, order: 'recently_updated' }),
            this.searchPrograms({ page: 1, order: 'recently_updated', source: 'custom' }),
            this.searchPrograms({ page: 1, order: 'recently_updated', source: 'lean_on' }),
        ]);
        return {
            all: allData?.total ?? 0,
            custom: customData?.total ?? 0,
            lean_on: leanOnData?.total ?? 0,
        };
    }

    public async getTags(): Promise<ProgramTag[]> {
        const { data, error } = await this.programTagService.getAll();
        if (error || !data) {
            return [];
        }
        return data;
    }

    public createTag(name: string) {
        const request: ProgramTagRequest = { name: name.trim() };
        return this.programTagService.create(request);
    }

    public updateTag(id: number, name: string, description?: string) {
        const request: ProgramTagRequest = {
            name: name.trim(),
            description,
        };
        return this.programTagService.update(id, request);
    }

    public deleteTag(id: number) {
        return this.programTagService.delete(id);
    }

    public async applyTagToPrograms(tagId: number, programIds: number[]): Promise<{ error?: unknown }> {
        const results = await Promise.all(programIds.map(async programId => {
            const { data, error } = await this.get(programId);
            if (error || !data) {
                return { error };
            }

            const tagged = data as ProgramResponse & { tag_ids?: number[]; tags?: ProgramTag[] };
            const currentTagIds = tagged.tag_ids ?? tagged.tags?.map(tag => tag.id) ?? [];
            if (currentTagIds.includes(tagId)) {
                return { error: undefined };
            }

            const program = this.responseToProgram(data);
            program.tag_ids = [...currentTagIds, tagId];
            return this.update(programId, this.mapToProgramRequest(program));
        }));

        return results.find(result => result.error) ?? { error: undefined };
    }

    public updateStage(id: number, stage: ProgramStageKey) {
        return this.fitApi.put<ProgramStageUpdate>(`/admin/program/${id}/stage`, { stage });
    }

    public updateStages(programIds: number[], stage: ProgramStageKey) {
        const request: ProgramBulkStageRequest = {
            program_ids: programIds,
            stage,
        };
        return this.fitApi.put<unknown>('/admin/program/stage', request);
    }

    public restore(id: number) {
        return this.fitApi.put<ProgramResponse>(`/admin/program/${id}/restore`, null);
    }

    public searchEquipments(query: string, page: number) {
        return this.equipmentService.search(query, page);
    }

    public getAllEquipments() {
        return this.equipmentService.all();
    }

    public getAllCategories() {
        return this.programCategoryService.list();
    }

    public getAllLevels() {
        return this.levelService.all();
    }

    public getAllEnvironments() {
        return this.environmentService.all();
    }

    public getAllGoals() {
        return this.goalService.all();
    }

    public getAllInstructors() {
        return this.instructorService.all();
    }

    public searchPublished(
        query: string,
        page: number,
        is_tailor_made?: boolean
    ) {
        const params: any = {
            search: query,
            page: page.toString(),
            is_tailor_made,
            is_published: true
        };
        return this.fitApi.get<FitList<ProgramSearch>>(
            '/admin/program',
            params
        );
    }

    public searchForUser(
        userId: number,
        query: string,
        page: number
    ) {
        return this.fitApi.get<FitList<ProgramSearch>>(
            `/admin/user/${userId}/program`,
            {
                search: query,
                page: page.toString()
            }
        );
    }

    public get(id: number) {
        return this.fitApi.get<ProgramResponse>('/admin/program/' + id);
    }

    public getProgramUsers(programId: number) {
        return this.fitApi.get<Customer[]>(`/admin/program/${programId}/user`);
    }

    public create(request: ProgramRequest) {
        return this.fitApi.put<ProgramResponse>('/admin/program/0', request);
    }

    public update(requestId: number, request: ProgramRequest) {
        return this.fitApi.put<ProgramResponse>(
            '/admin/program/' + requestId,
            request
        );
    }

    public delete(id: number) {
        return this.fitApi.delete<any>('/admin/program/' + id);
    }

    public deactivateForAll(id: number) {
        return this.fitApi.put<any>(`/admin/program/${id}/deactivate`, null);
    }

    public deactivateForUser(programId: number, userId: number) {
        return this.fitApi.post<unknown>(`/admin/program/${programId}/user/${userId}/deactivate`, {});
    }

    public reactivateForAll(id: number) {
        return this.fitApi.put<any>(`/admin/program/${id}/reactivate`, null);
    }

    public searchCategories(query: string, page: number) {
        return this.programCategoryService.search(query, page);
    }

    public searchLevels(query: string, page: number) {
        return this.levelService.search(query, page);
    }

    public searchEnvironments(query: string, page: number) {
        return this.environmentService.search(query, page);
    }

    public searchGoals(query: string, page: number) {
        return this.goalService.search(query, page);
    }

    public searchMuscleGroups(query: string, page: number) {
        return this.muscleGroupsService.search(query, page);
    }

    public searchInstructors(query: string, page: number) {
        return this.instructorService.search(query, page);
    }

    public publishProgram(programId: number) {
        return this.fitApi.put<any>(`/admin/program/${programId}/publish`, null);
    }

    public assignProgramToUser(programId: number, userId: number, startDate?: string) {
        const params = startDate ? { start_date: startDate } : undefined;
        return this.fitApi.put<any>(`/admin/program/${programId}/user/${userId}`, undefined, params);
    }

    public assignTailorProgramToUser(
        programId: number,
        userId: number,
        price: number,
        currencyCode: string
    ) {
        return this.fitApi.post<any>(`/admin/program/${programId}/user/${userId}/paid`, {
            program: programId,
            user: userId,
            price: `${price}`,
            currencyCode
        }
        );
    }

    public unassignUserFromTailorProgram(program: Program, user: User) {
        return this.fitApi.post<any>(`/admin/program/${program.id}/user/${user.id}/deactivate`, {});
    }

    public genenerateUniqueID() {
        return Date.now();
    }

    public copyToProgramOrTailor(program: Program, params) {
        return this.fitApi.put<any>(`/admin/program/${program.id}/copy`, null, params);
    }

    public mapToProgramRequest(program: Program): ProgramRequest {
        const browserTimezone = getBrowserTimezone();
        let publishAtUtc: string = null;
        
        // Convert publish_at from user timezone to UTC if it exists
        if (program.publish_at) {
            if (typeof program.publish_at === 'string') {
                // If it's already a string (from backend), parse it first
                publishAtUtc = zonedTimeToUtc(new Date(program.publish_at), browserTimezone).toISOString();
            } else {
                // If it's a Date object from the datepicker
                publishAtUtc = zonedTimeToUtc(program.publish_at as any, browserTimezone).toISOString();
            }
        }
        
        return {
            name: program.name,
            internal_name: program.internal_name,
            description: program.description,
            exercise_environment_ids: program.exercise_environment_ids,
            category_ids: program.category_ids,
            level_ids: program.level_ids,
            goal_ids: program.goal_ids,
            instructors: (program.instructors ?? []).map((instructor, index): ProgramInstructorRequest => ({
                id: instructor.id,
                sort_order: index + 1
            })),
            image_id: program.image?.id ?? null,
            video_id: program.video ? program.video.id : null,
            rest_day_image_id: program.rest_day_image
                ? program.rest_day_image.id
                : undefined,
            is_free: program.is_free,
            is_tailor_made: program.is_tailor_made,
            is_coming_soon: program.is_coming_soon,
            follows_schedule: program.follows_schedule,
            parent_id: program.parent_program ? program.parent_program.id : null,
            stripe_product_id: program.stripe_product_id,
            stripe_payment_link: program.stripe_payment_link,
            shopify_product_id: program.shopify_product_id,
            order: program.order,
            gender: program.gender,
            language: program.language ?? null,
            is_published: program.is_published,
            publish_at: publishAtUtc,
            expected_results: (program.expected_results ?? []).map(
                (e) => e.description
            ),
            muscle_group_ids: (program.muscle_groups ?? []).map((mg) => mg.id),
            gallery_ids: (program.documents ?? []).map(item => item.id),
            program_weeks: (program.weeks ?? []).map((w, i) => {
                return {
                    order: i + 1,
                    description: w.description,
                    program_week_items: w.items
                        ? w.items.map((item, index) => {
                            return {
                                is_locked: item.is_locked,
                                order: item.order ?? index + 1,
                                training_id: item.training_id,
                                make_copy: item.make_copy ?? false,
                                is_rest_day: item.is_rest_day,
                                alternative_training_ids: item.alternative_training_ids ?? [],
                            };
                        })
                        : [],
                };
            }),
            workout_days_per_week: program.workout_days_per_week,
            price: program.one_time_purchase ? program.price : null,
            questionnaire_id: program.questionnaire ? program.questionnaire.id : null,
            readiness_questionnaire_id: program.readiness_questionnaire ? program.readiness_questionnaire.id : null,
            include_in_survey: program.include_in_survey ?? false,
            currency_code: program.one_time_purchase ? program.currency_code : null,
            only_with_one_time_payment: program.only_with_one_time_payment,
            package_id: program.package_id,
            number_of_renewals: program.number_of_renewals || null,
            continue_renewing: program.continue_renewing,
            notify_coach_on_purchase: program.notify_coach_on_purchase,
            program_packages: program.only_with_one_time_payment ? program.program_packages : [],
            tag_ids: program.tag_ids ?? [],
            min_age: program.min_age ?? null,
            max_age: program.max_age ?? null,
            live_sync: program.live_sync ?? false,
        };
    }

    public responseToProgram(programResponse: ProgramResponse): Program {
        const browserTimezone = getBrowserTimezone();
        let publishAtLocal: string | Date = null;

        if (programResponse.publish_at) {
            publishAtLocal = utcToZonedTime(new Date(programResponse.publish_at), browserTimezone);
        }

        const tagged = programResponse as ProgramResponse & { tag_ids?: number[]; tags?: ProgramTag[]; min_age?: number; max_age?: number };

        return {
            id: programResponse.id,
            name: programResponse.name,
            internal_name: programResponse.internal_name,
            description: programResponse.description,
            exercise_environment_ids: programResponse.exercise_environments
                ? programResponse.exercise_environments.map(e => e.id)
                : [],
            goal_ids: programResponse.goals ? programResponse.goals.map(g => g.id) : [],
            image_id: programResponse.image ? programResponse.image.id : undefined,
            image: programResponse.image ? programResponse.image : undefined,
            is_free: programResponse.is_free,
            is_display_only: programResponse.is_display_only,
            is_tailor_made: programResponse.is_tailor_made,
            is_coming_soon: programResponse.is_coming_soon,
            follows_schedule: programResponse.follows_schedule,
            parent_program: programResponse?.parent_program || null,
            stripe_product_id: programResponse.stripe_product_id,
            stripe_payment_link: programResponse.stripe_payment_link,
            shopify_product_id: programResponse.shopify_product_id,
            order: programResponse.order,
            gender: programResponse.gender,
            language: programResponse.language?.code ?? null,
            is_published: programResponse.is_published,
            publish_at: publishAtLocal,
            category_ids: programResponse.categories ? programResponse.categories.map(c => c.id) : [],
            level_ids: programResponse.levels ? programResponse.levels.map(l => l.id) : [],
            video_id: programResponse.video ? programResponse.video.id : null,
            video: programResponse.video,
            weeks: (programResponse.program_weeks ?? [])
                .slice()
                .sort((w1, w2) => (w1.order > w2.order ? 1 : -1))
                .map(w => ({
                    id: w.id,
                    description: w.description,
                    items: w.week_items
                        ? w.week_items.map(i => ({
                            is_locked: i.is_locked,
                            training_id: i.training_id ?? i.training?.id ?? 0,
                            name: i.training?.name ?? i.alternatives?.[0]?.name ?? '',
                            is_rest_day: i.is_rest_day,
                            order: i.order,
                            duration: i.training?.approximate_duration_in_minutes ?? null,
                            typeName: this.formatTrainingTypeNames(i.training?.training_type_names),
                            is_program_fork: i.is_program_fork ?? false,
                            training_copy_of: i.training_copy_of ?? null,
                            alternative_training_ids: i.alternative_training_ids ?? i.alternatives?.map(alt => alt.id) ?? [],
                            alternatives: i.alternatives ?? [],
                        }))
                        : [],
                })),
            workout_days_per_week: programResponse.workout_days_per_week,
            expected_results: programResponse.expected_results,
            rest_day_image: programResponse.rest_day_image,
            muscle_groups: programResponse.muscle_groups ?? [],
            instructors: (programResponse.instructors ?? []).map(instructor => ({
                id: instructor.id,
                name: instructor.name,
                order: instructor.order,
            })),
            documents: programResponse.documents || [],
            price: programResponse.price,
            currency_code: programResponse.currency_code,
            questionnaire: programResponse?.questionnaire || null,
            readiness_questionnaire: programResponse?.readiness_questionnaire || null,
            include_in_survey: programResponse.include_in_survey,
            one_time_purchase: +programResponse.price > 0
                || programResponse.only_with_one_time_payment
                || (programResponse.program_packages ?? []).length > 0,
            only_with_one_time_payment: programResponse.only_with_one_time_payment,
            package_id: programResponse.package_id,
            continue_renewing: programResponse.continue_renewing,
            number_of_renewals: programResponse.number_of_renewals,
            notify_coach_on_purchase: programResponse.notify_coach_on_purchase,
            program_packages: programResponse.program_packages ?? [],
            tag_ids: tagged.tag_ids ?? tagged.tags?.map(tag => tag.id) ?? [],
            min_age: tagged.min_age ?? null,
            max_age: tagged.max_age ?? null,
            stage: programResponse.stage,
            live_sync: programResponse.live_sync ?? false,
            activated_count: programResponse.activated_count ?? 0,
        };
    }

    public emptyProgram(): Program {
        return {
            name: '',
            internal_name: '',
            description: '',
            exercise_environment_ids: [],
            goal_ids: [],
            image_id: undefined,
            image: undefined,
            is_free: false,
            is_display_only: false,
            is_tailor_made: false,
            is_coming_soon: false,
            follows_schedule: true,
            parent_program: null,
            stripe_product_id: null,
            stripe_payment_link: null,
            shopify_product_id: null,
            order: null,
            gender: undefined,
            language: null,
            is_published: false,
            publish_at: null,
            category_ids: [],
            level_ids: [],
            video_id: undefined,
            video: undefined,
            weeks: [],
            workout_days_per_week: 3,
            expected_results: [],
            rest_day_image: undefined,
            muscle_groups: [],
            instructors: [],
            documents: [],
            currency_code: null,
            price: null,
            questionnaire: null,
            readiness_questionnaire: null,
            include_in_survey: false,
            one_time_purchase: false,
            only_with_one_time_payment: false,
            package_id: null,
            number_of_renewals: null,
            continue_renewing: false,
            notify_coach_on_purchase: false,
            program_packages: [],
            tag_ids: [],
            min_age: null,
            max_age: null,
            live_sync: false,
            activated_count: 0,
        };
    }

    private buildSearchParams(params: ProgramSearchParams): Record<string, string> {
        const apiParams: Record<string, string> = {
            search: params.search ?? '',
            page: String(params.page ?? 1),
        };

        apiParams.order = params.order ?? 'recently_updated';

        if (params.source && params.source !== 'all') {
            apiParams.source = params.source;
        }

        if (params.is_published === true || params.is_published === false) {
            apiParams.is_published = params.is_published ? 'true' : 'false';
        }

        if (params.is_free === true || params.is_free === false) {
            apiParams.is_free = params.is_free ? 'true' : 'false';
        }

        if (params.tag_ids?.length) {
            apiParams.tag_ids = params.tag_ids.join(',');
        }
        if (params.program_category_ids?.length) {
            apiParams.program_category_ids = params.program_category_ids.join(',');
        }
        if (params.level_ids?.length) {
            apiParams.level_ids = params.level_ids.join(',');
        }
        if (params.program_length_filter_ids?.length) {
            apiParams.program_length_filter_ids = params.program_length_filter_ids.join(',');
        }
        if (params.environment_ids?.length) {
            apiParams.environment_ids = params.environment_ids.join(',');
        }
        if (params.equipment_ids?.length) {
            apiParams.equipment_ids = params.equipment_ids.join(',');
        }
        if (params.stage_ids?.length) {
            apiParams.stage_ids = params.stage_ids.join(',');
        }
        if (params.per_page != null) {
            apiParams.per_page = String(params.per_page);
        }

        return apiParams;
    }

    private formatTrainingTypeNames(value?: string): string {
        if (!value?.trim()) {
            return '';
        }

        const names = value.split(',').map(name => name.trim()).filter(Boolean);
        if (!names.length) {
            return '';
        }
        if (names.length === 1) {
            return names[0];
        }
        return `${names[0]} +${names.length - 1}`;
    }
}
